/**
 * Maaş Hareket Ekle Modal'ı — manuel ekleme/kesinti.
 * BOSS + Muhasebe.
 */

import { useEffect, useState } from 'react'
import { Plus, Minus, Save, AlertCircle } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/contexts/AuthContext'
import {
  maasHareketiOlustur,
  type MaasHareketTipi,
} from '@/lib/services/maas-hareket-service'
import type { KullaniciPersonel } from '@/types'

interface Props {
  acik: boolean
  personel: KullaniciPersonel | null
  donem: string
  baslangicTipi: MaasHareketTipi
  onKapat: () => void
  onKaydedildi?: () => void
}

export function MaasHareketEkleModal({
  acik, personel, donem, baslangicTipi, onKapat, onKaydedildi,
}: Props) {
  const { firebaseUser, kullanici } = useAuth()
  const [tip, setTip] = useState<MaasHareketTipi>(baslangicTipi)
  const [tutar, setTutar] = useState('')
  const [aciklama, setAciklama] = useState('')
  const [gonderiliyor, setGonderiliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    if (!acik) return
    setTip(baslangicTipi)
    setTutar('')
    setAciklama('')
    setHata(null)
  }, [acik, baslangicTipi])

  async function kaydet() {
    if (!firebaseUser || !personel) return
    const tutarNum = Number(tutar.replace(',', '.'))
    if (!tutarNum || tutarNum <= 0) {
      setHata('Geçerli tutar gir (pozitif)')
      return
    }
    if (!aciklama.trim()) {
      setHata('Açıklama zorunlu')
      return
    }

    const rol = kullanici?.rol === 'boss' ? 'boss' : 'yonetici'

    setGonderiliyor(true)
    setHata(null)
    try {
      await maasHareketiOlustur({
        personelId: personel.id,
        personelIsim: personel.isim,
        donem,
        tip,
        tutar: tutarNum,
        aciklama: aciklama.trim(),
        olusturanId: firebaseUser.uid,
        olusturanRol: rol,
      })
      onKaydedildi?.()
      onKapat()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setGonderiliyor(false)
    }
  }

  if (!personel) return null

  const ekleme = tip === 'ekleme'
  const tipRengi = ekleme
    ? 'bg-green-600 hover:bg-green-700'
    : 'bg-red-600 hover:bg-red-700'

  return (
    <Modal
      acik={acik}
      onKapat={onKapat}
      baslik={ekleme ? 'Maaşa Ekleme' : 'Maaşa Kesinti'}
      boyut="md"
    >
      <div className="space-y-4">
        <div className={`rounded-xl border-2 p-3 ${
          ekleme ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
        }`}>
          <p className="text-sm font-semibold text-gray-900">{personel.isim}</p>
          <p className="text-xs text-gray-700 mt-0.5">
            {donem} dönemi · {ekleme ? '🟢 Ekleme' : '🔴 Kesinti'}
          </p>
        </div>

        {/* Tip seçici */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Tip
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setTip('ekleme')}
              className={`px-3 py-2.5 rounded-xl border-2 font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                tip === 'ekleme'
                  ? 'bg-green-50 border-green-500 text-green-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <Plus className="w-4 h-4" />
              Ekleme
            </button>
            <button
              type="button"
              onClick={() => setTip('kesinti')}
              className={`px-3 py-2.5 rounded-xl border-2 font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                tip === 'kesinti'
                  ? 'bg-red-50 border-red-500 text-red-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <Minus className="w-4 h-4" />
              Kesinti
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Tutar (₺) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={tutar}
            onChange={(e) => setTutar(e.target.value)}
            placeholder="Örn: 250"
            className="input-base text-lg font-bold tabular-nums"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Açıklama <span className="text-red-500">*</span>
          </label>
          <textarea
            value={aciklama}
            onChange={(e) => setAciklama(e.target.value)}
            rows={3}
            maxLength={300}
            placeholder={
              ekleme
                ? 'Örn: Ekstra performans ödülü, fark ödemesi...'
                : 'Örn: Kasada 250 TL eksik, kırılan ürün bedeli...'
            }
            className="input-base resize-none"
          />
          <p className="text-[10px] text-gray-500 mt-1">
            Personel bu açıklamayı görecek. Net ve açık yaz.
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900">
            {kullanici?.rol === 'boss' ? (
              <>Bu işlem personelin maaşına <strong>anlık</strong> yansıyacak ve personel kendi alacak ekranında görecek.</>
            ) : (
              <>Bu işlem <strong>BOSS onayına</strong> gönderilecek. Onaylanana kadar maaşa yansımaz.</>
            )}
          </p>
        </div>

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onKapat}
            disabled={gonderiliyor}
            className="btn-secondary flex-1"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={kaydet}
            disabled={gonderiliyor}
            className={`flex-1 px-4 py-2 rounded-xl text-white font-semibold inline-flex items-center justify-center gap-2 ${tipRengi}`}
          >
            <Save className="w-4 h-4" />
            {gonderiliyor ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
